import React, { useEffect, useState, useCallback } from 'react';
import { AdminPageHead } from '../components/AdminPageHead';
import {
    Box, Card, CardContent, Typography, TextField, Button, Chip,
    Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
    TablePagination, CircularProgress, Dialog, DialogTitle, DialogContent,
    DialogActions, Tooltip, Divider, IconButton, InputAdornment, Snackbar, Alert,
} from '@mui/material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { connect } from 'react-redux';
import { AdminDashboardService } from '../../../../../core/services/admin-dashboard.service';

const adminService = new AdminDashboardService();

interface Props {
    userData: any;
}

interface CouponRow {
    id: number;
    coupon_code: string;
    username: string;
    influencer_id: number;
    account_id: number;
    special_amount: number;
    currency: string;
    max_uses: number;
    used_count: number;
    completed_uses?: number;
    status: 'Active' | 'Disabled' | 'Expired';
    created_at: string;
}

interface RedemptionRow {
    id: number;
    influencer_id: number;
    influencer_name: string | null;
    order_id: string;
    payment_id: string;
    final_amount: number;
    status: 'pending' | 'completed' | 'failed';
    redeemed_at: string | null;
    created_at: string;
}

const fmtDate = (v: string | null) =>
    v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const SpecialCouponManager: React.FC<Props> = ({ userData }) => {
    // --- generate ---
    const [username, setUsername] = useState('');
    const [generating, setGenerating] = useState(false);
    const [genError, setGenError] = useState('');
    const [lastCoupon, setLastCoupon] = useState<CouponRow | null>(null);

    // --- list ---
    const [coupons, setCoupons] = useState<CouponRow[]>([]);
    const [total, setTotal] = useState(0);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // --- redemptions dialog ---
    const [redemptionTarget, setRedemptionTarget] = useState<CouponRow | null>(null);
    const [redemptions, setRedemptions] = useState<RedemptionRow[]>([]);
    const [redemptionsLoading, setRedemptionsLoading] = useState(false);

    // --- revoke ---
    const [revokeTarget, setRevokeTarget] = useState<CouponRow | null>(null);
    const [revoking, setRevoking] = useState(false);

    // --- copy toast ---
    const [copied, setCopied] = useState(false);

    const loadCoupons = useCallback(async () => {
        setListLoading(true);
        setListError('');
        try {
            const res = await adminService.getSpecialCoupons({ q: search.trim(), page: page + 1, limit: rowsPerPage });
            setCoupons(Array.isArray(res?.data?.rows) ? res.data.rows : []);
            setTotal(res?.data?.total || 0);
        } catch {
            setListError('Failed to load coupons.');
        } finally {
            setListLoading(false);
        }
    }, [search, page, rowsPerPage]);

    useEffect(() => { loadCoupons(); }, [loadCoupons]);

    const handleGenerate = async () => {
        const u = username.trim();
        if (u.length < 2) { setGenError('Please enter a valid username (min 2 characters).'); return; }
        setGenerating(true);
        setGenError('');
        setLastCoupon(null);
        try {
            const res = await adminService.generateSpecialCoupon({ username: u, created_by: userData?.user_id });
            if (res?.status === 1 && res?.data?.rows?.[0]) {
                setLastCoupon(res.data.rows[0]);
                setUsername('');
                setPage(0);
                loadCoupons();
            } else {
                setGenError(res?.info || 'Failed to generate coupon. Please try again.');
            }
        } catch (e: any) {
            setGenError(e?.message || 'Failed to generate coupon. Please try again.');
        } finally {
            setGenerating(false);
        }
    };

    const copyCode = (code: string) => {
        try {
            navigator.clipboard.writeText(code);
            setCopied(true);
        } catch { /* clipboard unavailable */ }
    };

    const openRedemptions = async (coupon: CouponRow) => {
        setRedemptionTarget(coupon);
        setRedemptions([]);
        setRedemptionsLoading(true);
        try {
            const res = await adminService.getSpecialCouponRedemptions(coupon.id);
            setRedemptions(Array.isArray(res?.data?.rows) ? res.data.rows : []);
        } catch {
            /* silent */
        } finally {
            setRedemptionsLoading(false);
        }
    };

    const handleRevoke = async () => {
        if (!revokeTarget) return;
        setRevoking(true);
        try {
            await adminService.revokeSpecialCoupon(revokeTarget.id);
            setRevokeTarget(null);
            loadCoupons();
        } catch {
            /* silent */
        } finally {
            setRevoking(false);
        }
    };

    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>

            <AdminPageHead
                title="Special Coupons"
                subtitle="Coupons (username-XXXX) that give a creator the yearly plan at a fixed price — usable by up to 4 people (the creator + 3 friends). Web only."
                actions={
                    <Tooltip title="Refresh list">
                        <IconButton onClick={loadCoupons} size="small" disabled={listLoading}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                }
            />

            {/* Generate section */}
            <Card variant="outlined" sx={{ mb: 3, background: '#faf7ff' }}>
                <CardContent>
                    <Typography variant="subtitle2" fontWeight={600} mb={1}>
                        Generate a Coupon
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                        Enter the influencer's username. The coupon code will be generated as <strong>username-XXXX</strong> (random 4 digits), unique and usable 4 times.
                    </Typography>

                    <Box display="flex" alignItems="flex-start" gap={2} flexWrap="wrap">
                        <TextField
                            size="small"
                            label="Influencer Username"
                            placeholder="e.g. riyaverma"
                            sx={{ width: 340, background: '#fff' }}
                            value={username}
                            onChange={(e) => { setUsername(e.target.value); setGenError(''); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleGenerate(); }}
                            error={!!genError}
                            helperText={genError || ''}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={generating ? <CircularProgress size={16} /> : <LocalOfferOutlinedIcon />}
                            disabled={generating || username.trim().length < 2}
                            onClick={handleGenerate}
                            sx={{ mt: 0.5 }}
                        >
                            {generating ? 'Generating...' : 'Generate Coupon'}
                        </Button>
                    </Box>

                    {lastCoupon && (
                        <Box mt={2} p={1.5} sx={{ border: '1px dashed #7c3aed', borderRadius: 1.5, background: '#fff', display: 'inline-flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <CheckCircleOutlineIcon sx={{ color: '#16a34a' }} />
                            <Box>
                                <Typography variant="caption" color="text.secondary" display="block">New coupon code</Typography>
                                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: 'monospace', letterSpacing: 0.5 }}>
                                    {lastCoupon.coupon_code}
                                </Typography>
                            </Box>
                            <Tooltip title="Copy code">
                                <IconButton size="small" onClick={() => copyCode(lastCoupon.coupon_code)}>
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Chip size="small" variant="outlined" label={`₹${lastCoupon.special_amount} / year`} />
                            <Chip size="small" variant="outlined" label={`${lastCoupon.max_uses} uses`} />
                            {lastCoupon.influencer_id > 0
                                ? <Chip size="small" color="success" variant="outlined" label={`Linked to influencer #${lastCoupon.influencer_id}`} />
                                : <Chip size="small" color="warning" variant="outlined" label="Username not yet registered" />}
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Divider sx={{ mb: 3 }} />

            {/* Coupons table */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
                <Typography variant="subtitle2" fontWeight={600}>Generated Coupons</Typography>
                <TextField
                    size="small"
                    placeholder="Search code or username"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <LocalOfferOutlinedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ width: 280 }}
                />
            </Box>

            {listError && <Typography color="error" variant="body2" mb={1}>{listError}</Typography>}

            <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 760 }}>
                    <TableHead>
                        <TableRow sx={{ background: '#f5f5f5' }}>
                            <TableCell><strong>Coupon Code</strong></TableCell>
                            <TableCell><strong>Username</strong></TableCell>
                            <TableCell><strong>Linked Influencer</strong></TableCell>
                            <TableCell><strong>Price</strong></TableCell>
                            <TableCell><strong>Uses</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Created</strong></TableCell>
                            <TableCell align="center"><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {listLoading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                    <CircularProgress size={24} />
                                </TableCell>
                            </TableRow>
                        ) : coupons.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                    No coupons generated yet.
                                </TableCell>
                            </TableRow>
                        ) : coupons.map((row) => {
                            const used = row.completed_uses ?? row.used_count ?? 0;
                            return (
                                <TableRow key={row.id} hover>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                                                {row.coupon_code}
                                            </Typography>
                                            <Tooltip title="Copy code">
                                                <IconButton size="small" onClick={() => copyCode(row.coupon_code)}>
                                                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{row.username || '—'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        {row.influencer_id > 0
                                            ? <Chip size="small" variant="outlined" label={`#${row.influencer_id}`} />
                                            : <Typography variant="caption" color="text.disabled">Not registered</Typography>}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">₹{row.special_amount}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={`${used} / ${row.max_uses}`}
                                            color={used >= row.max_uses ? 'default' : 'primary'}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {row.status === 'Active'
                                            ? <Chip label="Active" size="small" color="success" icon={<CheckCircleOutlineIcon />} />
                                            : <Chip label={row.status} size="small" color="default" />}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary">{fmtDate(row.created_at)}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box display="flex" gap={0.5} justifyContent="center">
                                            <Tooltip title="View who used it">
                                                <IconButton size="small" onClick={() => openRedemptions(row)}>
                                                    <PeopleOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {row.status === 'Active' && (
                                                <Tooltip title="Disable coupon">
                                                    <IconButton size="small" color="error" onClick={() => setRevokeTarget(row)}>
                                                        <BlockOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                {total > 0 && (
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 25, 50]}
                    />
                )}
            </TableContainer>

            {/* Redemptions Dialog */}
            <Dialog open={!!redemptionTarget} onClose={() => setRedemptionTarget(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleOutlineIcon />
                    Redemptions — {redemptionTarget?.coupon_code}
                </DialogTitle>
                <DialogContent dividers>
                    {redemptionsLoading ? (
                        <Box textAlign="center" py={3}><CircularProgress size={22} /></Box>
                    ) : redemptions.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" py={2} textAlign="center">
                            No one has used this coupon yet.
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>User</strong></TableCell>
                                    <TableCell><strong>Amount</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell><strong>Date</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {redemptions.map((r) => (
                                    <TableRow key={r.id}>
                                        <TableCell>
                                            <Typography variant="body2">{r.influencer_name || `#${r.influencer_id}`}</Typography>
                                        </TableCell>
                                        <TableCell>₹{r.final_amount}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={r.status}
                                                color={r.status === 'completed' ? 'success' : r.status === 'pending' ? 'warning' : 'default'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                                {fmtDate(r.redeemed_at || r.created_at)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRedemptionTarget(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Revoke Confirm */}
            <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Disable Coupon?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        This will disable <strong>{revokeTarget?.coupon_code}</strong>. It can no longer be used for new purchases. Existing subscriptions are unaffected.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRevokeTarget(null)} disabled={revoking}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleRevoke}
                        disabled={revoking}
                        startIcon={revoking ? <CircularProgress size={16} /> : <BlockOutlinedIcon />}
                    >
                        {revoking ? 'Disabling...' : 'Disable'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={copied}
                autoHideDuration={1500}
                onClose={() => setCopied(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" variant="filled" sx={{ fontSize: 13 }}>Coupon code copied!</Alert>
            </Snackbar>
        </Box>
    );
};

const mapStateToProps = (state: any) => ({
    userData: state.UserReducer.UserData,
});

const connectToRedux = connect(mapStateToProps)(SpecialCouponManager);
export { connectToRedux as SpecialCouponManager };
